import { useMemo } from "react";
import { FIELD_STATS, POI_STATS } from "./statIconModels";
import { Tooltip, PlacesType } from "react-tooltip";
import DataItem from "shared/components/DataItem";
import PortalledContent from "shared/components/PortalledContent";

interface StatIconProps<T> {
  targetObject: T;
  targetKey: T extends "field"
    ? keyof typeof FIELD_STATS
    : keyof typeof POI_STATS;
  value: string | number | null;
  imperialUnits: boolean;
  //
  iconSize: number;
  /**
   * NOTE: That the value here should be unique for each location
   * (e.g. parent component)
   */
  tooltipTarget?: "mainSidePanel" | "farmDetailsPanel" | "poiDetailsPanel";
  tooltipContentKey?:
    | keyof Pick<
        ReturnType<typeof getStatDetails>,
        "name" | "displayName" | "description"
      >;
  tooltipPlace?: PlacesType;
  parentClassNames?: string;
  valueClassNames?: string;
  viewType: "mini" | "dataItem";
}

function StatIcon<T extends "field" | "poi">({
  viewType,
  targetObject,
  targetKey,
  value,
  imperialUnits,
  //
  iconSize,
  tooltipTarget,
  tooltipContentKey = "displayName",
  tooltipPlace = "right",
  parentClassNames = "",
  valueClassNames = "",
}: StatIconProps<T>): React.ReactElement | null {
  const statDetails = useMemo(
    () => getStatDetails(targetObject, targetKey),
    [targetObject, targetKey]
  );
  const {
    name,
    unit,
    svg: SVG,
    value: valueFunction,
    additionalClassNames,
  } = statDetails;

  const valueContent = valueFunction ? valueFunction(value) : value;
  if (!valueContent) {
    return null;
  }

  const tooltipId = `STAT_ICON_${tooltipTarget || ""}_${targetKey}`;
  // This adds fallback option when `tooltipContentKey` is "displayName"
  const tooltipContent =
    tooltipContentKey === "displayName"
      ? statDetails.displayName || statDetails.name
      : statDetails[tooltipContentKey];
  const hasTootltip = !!(tooltipTarget && tooltipContent);

  const statContent = (
    <>
      <div
        className={`${parentClassNames} ${hasTootltip ? "TooltipOnHover" : ""}`}
        data-tooltip-id={tooltipId}
      >
        <SVG
          height={iconSize}
          width={iconSize}
          role="img"
          aria-label={name}
          className={
            typeof additionalClassNames === "function"
              ? additionalClassNames(value)
              : additionalClassNames
          }
        />
        <span className={valueClassNames}>
          {valueContent}
          <span className="StatIconUnits">
            {typeof unit === "function" ? unit(imperialUnits) : unit}
          </span>
        </span>
      </div>
      {/* Tooltip not visible if no `tooltipTarget` given */}
      {hasTootltip && (
        <PortalledContent
          children={
            <Tooltip id={tooltipId} place={tooltipPlace} className="ToolTip">
              <div className="StatIconToolTip">{tooltipContent}</div>
            </Tooltip>
          }
        />
      )}
    </>
  );

  if (viewType === "mini") {
    return statContent;
  }
  return <DataItem left={name} middle={statContent} />;
}

function getStatDetails<T extends "field" | "poi">(
  targetObject: T,
  targetKey: T extends "field"
    ? keyof typeof FIELD_STATS
    : keyof typeof POI_STATS
) {
  return targetObject === "field"
    ? FIELD_STATS[targetKey as keyof typeof FIELD_STATS]
    : POI_STATS[targetKey as keyof typeof POI_STATS];
}

export default StatIcon;
export { getStatDetails };
